import * as cptable from 'client/../../node_modules/xlsx/dist/cpexcel.js'
import { ImportTypes } from 'client/components/Screen/ImportScreen/ImportScreen'
import { Mapping } from 'client/components/Screen/ImportScreen/MappingView/MappingView'
import { CustomerInterface } from 'client/Interfaces/CustomerInterface'
import { ProductForImportInterface, ProductTypesEnum } from 'client/Interfaces/ProductInterface'
import _ from 'lodash'
import * as XLSX from 'xlsx'

import { getFieldsForMapping } from './MappingHeader/MappingHeaderHelper'

XLSX.set_cptable(cptable)

export const extractEmails = (str: string) => {
  const regex = new RegExp(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi)
  const results = str.toLowerCase().match(regex)
  return results
}

export const parseFileToArray = (file: File) => {
  return new Promise<string[][]>((resolve, reject) => {
    try {
      const reader = new FileReader()
      reader.onload = (e) => {
        const ws = XLSX.read(e.target?.result, {
          codepage: 65001, // UTF-8 encoding
        })
        const firstSheet = ws.Sheets[ws.SheetNames[0]]
        const arr = XLSX.utils.sheet_to_json<string[]>(firstSheet, {
          header: 1,
          raw: false,
          blankrows: false,
          defval: '',
        })
        resolve(arr)
      }
      reader.readAsArrayBuffer(file)
    } catch (e) {
      reject(e)
    }
  })
}

export const getMatchedFieldName = (value: string) => {
  const translatedFields = getFieldsForMapping('CUSTOMER')

  let field = ''
  value = value.trim()

  if (value.match(/.*\@.*\.*/)) {
    field = 'email'
  } else if (value.match(/^\d{4,7}$/)) {
    field = 'address.pobox'
  } else if (value.match(/^\+{0,1}([\d -]{5,16})$/)) {
    field = 'phoneNumberWork'
  } else if (value.match(/^\d{4,7} [^\d]+$/)) {
    field = 'poboxCity'
  } else if (value.match(/^[\w \d]+ [\dA-Z]+$/)) {
    field = 'address.streetAddress1'
  } else {
    for (const key in translatedFields) {
      const element = translatedFields[key]

      if (value.toLowerCase() === element?.toLowerCase()) {
        return key
      }
    }
  }

  return field
}

interface FoundMatchedFields {
  [field: string]: {
    [col: number]: number // number matches in column
  }
}

export const getAutomapping = (content: string[][]) => {
  const foundSuitableFields: FoundMatchedFields = {}
  /* For example:
	{
		email: { '1': 10, '2': 1 },
		address.pobox: { '3': 15 }
		...
	} 
	*/

  _.forEach(content, (row) => {
    _.forEach(row, (col, i) => {
      const field = getMatchedFieldName(col)
      if (field !== '') {
        if (foundSuitableFields[field]) {
          foundSuitableFields[field][i] = foundSuitableFields[field][i] + 1 || 1
        } else {
          foundSuitableFields[field] = {}
          foundSuitableFields[field][i] = 1
        }
      }
    })
  })

  const newMapping: Mapping = {}
  _.forEach(foundSuitableFields, (values, field) => {
    const maxCol = _.maxBy(_.keys(values), (o) => values[o])
    newMapping[field] = Number(maxCol)
  })

  return newMapping
}

export const checkRequiredFields = (type: ImportTypes, mapping: Mapping) => {
  const errorFields: string[] = []

  if (type === 'CUSTOMER') {
    if (mapping.companyName === undefined) {
      errorFields.push('companyName')
      if (mapping.firstNameLastName === undefined) {
        errorFields.push('firstNameLastName')
        if (mapping.firstName === undefined && mapping.lastName === undefined) {
          errorFields.push('firstName', 'lastName')
          _.pull(errorFields, 'companyName', 'firstNameLastName')
        } else {
          _.pull(errorFields, 'companyName', 'firstNameLastName')
        }
      } else {
        _.pull(errorFields, 'companyName')
      }
    }
  }

  if (type === 'PRODUCT') {
    if (mapping.name === undefined) {
      errorFields.push('name')
    }
    if (mapping.vatPct === undefined) {
      errorFields.push('vatPct')
    }
    if (mapping.price === undefined) {
      errorFields.push('price')
    }
    if (mapping.total === undefined) {
      errorFields.push('total')
    }
  }

  return errorFields
}

export const generateCustomerPayload = (
  content: string[][],
  mapping: Mapping,
  language: string | number,
  paymentTime: string | number,
  userCountry?: string,
) => {
  const payload: CustomerInterface[] = []

  for (const row of content) {
    const newCustomer: CustomerInterface = {
      address: {
        country: userCountry,
      },
      customerType: 'p',
      language: String(language),
      paymentTerm: Number(paymentTime),
    }
    for (const field in mapping) {
      if (row[mapping[field]]) {
        const value = row[mapping[field]].trim()

        if (field === 'companyName' && value) {
          newCustomer.customerType = 'c'
        }

        if (value) {
          if (field === 'firstNameLastName') {
            const words = _.words(value)
            if (words[0]) {
              _.set(newCustomer, 'firstName', words[0])
            }
            if (words.length > 1) {
              _.set(newCustomer, 'lastName', _.tail(words).join(' '))
            }
          } else if (field === 'poboxCity') {
            const match = value.match(/^[A-Z0-9][A-Z0-9 \-\(\)]{2,} /) || []
            _.set(newCustomer, 'address.pobox', match[0] || '')
            _.set(newCustomer, 'address.city', value.replace(/^[A-Z0-9][A-Z0-9 \-\(\)]{2,} /, ''))
          } else if (field === 'cityPobox') {
            const match = value.match(/^[A-Z0-9][A-Z0-9 \-\(\)]{2,} /) || []
            _.set(newCustomer, 'address.pobox', match[0]?.trim || '')
            _.set(newCustomer, 'address.city', value.replace(/^[A-Z0-9][A-Z0-9 \-\(\)]{2,} /, ''))
          } else if (field === 'email') {
            const emails = extractEmails(value)
            if (emails && emails?.length > 0) {
              _.set(newCustomer, field, emails[0])
              _.set(newCustomer, 'emails', emails)
            }
          } else if (field === 'paymentTerm') {
            const numbers = value.match(/\d+/)
            if (numbers) {
              _.set(newCustomer, 'paymentTerm', numbers[0])
            }
          } else {
            _.set(newCustomer, field, value)
          }
        }
      }
    }

    payload.push(newCustomer)
  }

  return payload
}

export const generateProductPayload = (content: string[][], mapping: Mapping, productType: ProductTypesEnum) => {
  const payload: ProductForImportInterface[] = []

  for (const row of content) {
    const newProduct: ProductForImportInterface = {
      name: 'Empty',
      unit: 'h',
      price: 0,
      vatPct: 0,
      productType,
    }

    for (const field in mapping) {
      if (row[mapping[field]]) {
        const value = row[mapping[field]].trim()
        if (['price', 'total', 'vatPct'].includes(field)) {
          const prepareFloat = String(value).replaceAll(',', '.')
          const found = prepareFloat.match(/\d+(\.\d+)?/g) // 11.33adsqe3333 -> [11.33, 3333]
          const foundNumber = found !== null && found.length > 0 ? found[0] : 0
          let number = _.toNumber(foundNumber) || 0

          if (field === 'vatPct') {
            number = number / 100
          }
          _.set(newProduct, field, number)
        } else {
          _.set(newProduct, field, value)
        }
      }
    }

    payload.push(newProduct)
  }
  return payload
}

export interface ImportResult {
  fulfilled: number
  rejected: number
}

export const processImport = async (
  payload: (CustomerInterface | ProductForImportInterface)[],
  chunkLength: number,
  importFunc: (chunk: (CustomerInterface | ProductForImportInterface)[], current: number, total: number) => void,
) => {
  const results: ImportResult = { fulfilled: 0, rejected: 0 }
  const total = payload.length

  const chunks = _.chunk<CustomerInterface | ProductForImportInterface>(payload, chunkLength)

  for (let i = 0; i < chunks.length; i++) {
    try {
      await importFunc(chunks[i], i * chunkLength, total)
      results.fulfilled = results.fulfilled + chunks[i].length
    } catch (e) {
      console.error(e)
      results.rejected = results.rejected + chunks[i].length
    }
  }
  return results
}

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const isCustomerPayload = (
  arr: (CustomerInterface | ProductForImportInterface)[],
): arr is CustomerInterface[] => {
  return 'customerType' in arr[0] && 'address' in arr[0]
}

export const isProductPayload = (
  arr: (CustomerInterface | ProductForImportInterface)[],
): arr is ProductForImportInterface[] => {
  return 'name' in arr[0] && 'unit' in arr[0]
}
